<script setup lang="ts">
import { ref, watch, computed, onMounted, getCurrentInstance } from "vue";
import { asyncComputed } from '@vueuse/core';
import { useTranslation } from "i18next-vue";

import type { Group } from "@/types/group";
import type { DropdownItem } from "@/types/ui";

import { useTiming } from "@/mixins/timing";
import { useComponentErrorCapture } from "@/mixins/error";
import { isTouchEnabled } from "@/mixins/device";
import { useWindowDimension } from "@/mixins/windowDimension";
import { getIconName } from "@/mixins/icon";

import Draggable from "vuedraggable";

import UCSearch from "@/components/ui/UCSearch.vue";
import Dialog from "@/components/elements/Dialog.vue";
import GroupListItem from "@/components/elements/group/GroupListItem.vue";
import AddGroup from "@/components/elements/group/AddGroup.vue";
import EditGroup from "@/components/elements/group/EditGroup.vue";
import ListWithFilter from "@/components/elements/ListWithFilter.vue";
import ModalMinimal from "@/components/elements/ModalMinimal.vue";
import DropdownMenu from "@/components/ui/DropdownMenu.vue";

const { t } = useTranslation();
const { sleep } = useTiming();
useComponentErrorCapture();

const props = defineProps({
  allGroups: {
    type: Array,
    required: true,
  },
  selectedGroups: {
    type: Array,
    required: false,
    default: [],
  },
  dragGroup: {
    type: String,
    default: ''
  },
  dragItemHandle: {
    type: Boolean,
    default: true
  },
  page: {
    type: Object,
    required: false,
  },
  hasQuickOptions: {
    type: Boolean,
    default: false,
  },
});

const groupDropdownItems = [
  {
    icon: 'fa-light fa-plus',
    label: 'entity.add_to_page',
    value: 'add_to_page',
  },
  {
    icon: 'fa-light fa-pencil',
    label: 'ui.edit',
    value: 'edit',
  },
  {
    icon: 'fa-light fa-trash',
    label: 'ui.delete',
    value: 'delete',
  },
] as DropdownItem[];

const emit = defineEmits(["addGroups", "delete"]);

const { isSmallScreen } = useWindowDimension();

const elGroupList = ref();
const assignedGroups = ref<Group[]>([]);

const allGroups = ref<Group[]>([]);
const selectedGroups = ref<Group[]>([]);

const searchGroup = ref('');
const instance = getCurrentInstance() || {uid: Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000};

const reducedGroupList = ref<Group[]>([]);

const itemToModify = ref<Group | null>(null);
const itemToEdit = ref<Group | null>(null);
const itemToDelete = ref<Group | null>(null);
const dropdownedGroup = ref<Group | null>(null);
  
const modalAddGroup = ref();
const modalEditGroup = ref();
const dialogDeleteGroup = ref();

watch(props, (val) => {
  setLists();
}, { deep: true });

watch(searchGroup, () => {
  reducedGroupList.value = getReducedGroupList();
});

watch(reducedGroupList, async () => {
  if (itemToModify.value != null) {
    const newItemData = reducedGroupList.value.find((item) => {
      return item.group_id === itemToModify.value?.group_id;
    });
    if (newItemData) {
      itemToModify.value = newItemData;
    }
  }
});

const iconDrag = asyncComputed(async() => {
  return await getIconName('fa-grip-dots-vertical');
});

const buttonStyle = computed(() => {
  return isSmallScreen.value ? 'button--secondary' : 'button--tertiary';
});

function getReducedGroupList() {
  const skipGroups = selectedGroups.value.map((group) => {
    return group.group_id;
  });

  return allGroups.value.filter((group: Group) => {
    if (skipGroups.includes(group.group_id)) {
      return false;
    }
    return (
      group.name.toLowerCase().includes(searchGroup.value) ||
      (group.description || "").toLowerCase().includes(searchGroup.value)
    );
  });
}

function goTo(item: DropdownItem, group: Group) {
  switch (item.value) {
    case 'add_to_page':
      addToPage(group)
      break;
    case 'edit':
      editGroup(group);
      break;
    case 'delete':
      startDeleteGroup(group);
      break;
    default:
      return false;
  }
}

function dropdownVisibility(state: boolean, group: Group) {
  if (state == true) {
    dropdownedGroup.value = group;
  } else {
    dropdownedGroup.value = null;
  }
}

function assignAllGroups(array: Group []) {
  assignedGroups.value = []
  array.forEach(obj => {
    assignedGroups.value.push(obj);
  });
}

function deAssignAllGroups() {
  assignedGroups.value = []
}

function addGroupsToPage() {
  if (assignedGroups.value.length > 0) {
    emit("addGroups", assignedGroups.value);
    deAssignAllGroups();
  }
}

function isAssignedItem(group: Group) {
  return assignedGroups.value.findIndex((item: Group) => item.group_id === group.group_id) > -1;
}

function toggleItemCheckbox(group: Group) {
  const itemIndex = assignedGroups.value.findIndex((item: Group) => item.group_id === group.group_id);
  if (itemIndex > -1) {
    assignedGroups.value.splice(itemIndex, 1);
  } else {
    assignedGroups.value.push(group);
  }
}

function createGroup() {
  modalAddGroup.value.open();
}

function setLists() {
  allGroups.value = props.allGroups as Group[];
  selectedGroups.value = props.selectedGroups as  Group[];
  reducedGroupList.value = getReducedGroupList();
}

async function openItemModify(group: Group) {
  if (!isTouchEnabled()) {
    return false;
  }

  itemToModify.value = group;
}

function addToPage(group: Group | null) {
  if (group == null) {
    return;
  }

  const arrayMessage = [group];
  emit("addGroups", arrayMessage);
  itemToModify.value = null;
}

function editGroup(group: Group | null) {
  if (group == null) {
    return;
  }
  
  itemToEdit.value = group;
  modalEditGroup.value.open();
}

function startDeleteGroup(group: Group | null) {
  if (group == null) {
    return;
  }

  itemToDelete.value = group;
  dialogDeleteGroup.value.open();
}

function deleteGroup() {
  if (itemToDelete.value == null) {
    return;
  }

  emit("delete", itemToDelete.value);
  itemToDelete.value = null;
  itemToModify.value = null;
}

onMounted(() => {
  setLists();
})

</script>
<template>
  <ListWithFilter :skip-items-wrapper="true" class="lwf-group-list">
    <template #form>
      <div class="list-with-filter__search list-with-filter__search--small">
        <UCSearch
          v-model="searchGroup"
          :small="true"
        />
      </div>
    </template>
    <template #items>
      <Draggable
        v-model="reducedGroupList"
        :group="dragGroup"
        :force-fallback="true"
        ref="elGroupList"
        class="lwf-group-list__items"
        itemKey="group_id"
        handle=".entity-item__drag"
      >
        <template #item="{ element }">
          <div
            class="entity-item"
            :class="{'entity-item--selected': isAssignedItem(element) === true, 'entity-item--dropdowned': dropdownedGroup?.group_id == element.group_id }">
            <GroupListItem
              @click-meta="openItemModify"
              @edit="editGroup"
              :list-item="element"
              :inactive="true"
              :edit-button="true"
            >
              <template #checkbox>
                <div class="form-item form-item--checkbox-tick entity-item__checkbox-tick">
                  <input :id="`${instance.uid}-${element.entity_id}-checkbox-tick`" type="checkbox" :checked="isAssignedItem(element)" />
                  <label class="toggle" :for="`${instance.uid}-${element.entity_id}-checkbox-tick`" />
                  <button @click="toggleItemCheckbox(element)" class="button--toggle-tick"></button>
                </div>
              </template>
              <template #options>
                <DropdownMenu
                  @item-click="(item) => goTo(item, element)"
                  @show="(state) => dropdownVisibility(state, element)"
                  :list-data="groupDropdownItems"
                  :icon="'fa-light fa-edit'"
                  :title="element.name"
                  :on-right="true"
                  class="entity-item__dropdown-menu"
                />
              </template>
            </GroupListItem>
            <span
              v-if="isSmallScreen && hasQuickOptions"
              @click="openItemModify(element)"
              class="entity-item__quick-options">
              <i class="fa-regular fa-ellipsis-vertical"></i>
            </span>
            <span v-else-if="dragGroup.length > 0" class="entity-item__drag">
              <i v-if="iconDrag" class="fa-regular" :class="iconDrag"></i>
            </span> 
          </div>
        </template>
      </Draggable>
    </template>
    <template #footer>
      <div class="list-with-filter__footer">
        <div class="lwf-group-list__actions">
          <template v-if="!isSmallScreen && reducedGroupList.length > 0">
            <button
              v-if="reducedGroupList.length === assignedGroups.length && reducedGroupList.length > 0"
              @click="deAssignAllGroups()"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-xmark"></i>
              {{ $t("ui.clear_all") }}
            </button>
            <button
              v-else-if="reducedGroupList.length > 0"
              @click="assignAllGroups(reducedGroupList)"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-check"></i>
              {{ $t("ui.select_all") }}
            </button>
          </template>
          <button
            @click="createGroup()"
            class="button"
              :class="buttonStyle">
            <i class="fa-thin fa-circle-plus"></i>
            {{ $t("ui.new") }}
          </button>
          <Transition name="opacity-fast">
            <button
              v-show="assignedGroups.length > 0  && reducedGroupList.length > 0"
              @click="addGroupsToPage()"
              class="button"
              :class="buttonStyle">
              <i class="fa-thin fa-circle-arrow-right"></i>
              {{ $t("ui.add") }}
            </button>
          </Transition>
        </div>
      </div>
    </template>
  </ListWithFilter>
  <Teleport to="body">
    <ModalMinimal
      @close="itemToModify = null"
      :show="itemToModify != null"
      :name="'modal-group-options'"
      :title="itemToModify != null ? itemToModify?.name : ''"
      class="modal-minimal--item-options"
    >
      <div v-if="itemToModify != null" class="modal-minimal__list">
        <button @click="addToPage(itemToModify)">
          <i class="fa-light fa-plus"></i>
          <span>{{ $t("entity.add_to_page") }}</span>
        </button>
        <button @click="editGroup(itemToModify)">
          <i class="fa-light fa-pencil"></i> 
          <span>{{ $t("ui.edit") }}</span>
        </button>
        <button @click="startDeleteGroup(itemToModify)">
          <i class="fa-light fa-trash"></i> 
          <span>{{ $t("ui.delete") }}</span>
        </button>
      </div>
    </ModalMinimal>
  </Teleport>
  <Dialog
    ref="dialogDeleteGroup"
    @submit="deleteGroup"
    @close="itemToDelete = null"
    :title="$t('group.delete.title')"
    :text="$t('group.delete.question')"
    :submit-text="$t('ui.delete')"
    :cancel-text="$t('ui.cancel')"
  />
  <AddGroup
    ref="modalAddGroup"
    :page="page"
  />
  <EditGroup
    ref="modalEditGroup"
    :group="itemToEdit || {}"
  />
</template>
